import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../../data/lyraTherapy/clientSelectors'
import { useGetProviderPairCareHistoryQuery } from '../../../../messages/data/messagesApi'

export type CareTeamProviderProfile = { displayName: string; photoUrl: string }

export const useCareTeamProviderProfile = ({ skip }: { skip?: boolean } = {}) => {
  const intl = useIntl()
  const providerId = useSelector(getAuthUserId)
  const patientId = useSelector(getSelectedClientId)

  const { currentData: careHistory, isFetching } = useGetProviderPairCareHistoryQuery(
    !skip && providerId && patientId ? { providerId, patientId } : skipToken,
  )

  const resolver = useMemo(() => {
    const profilesById = new Map(careHistory?.map((provider) => [provider.participant_lyra_id, provider]))
    const unknownProvider = intl.formatMessage({
      defaultMessage: 'Unknown provider',
      description: 'Fallback name for a care team member whose provider profile is unavailable',
    })

    const getProviderProfile = (lyraId: string): CareTeamProviderProfile => {
      const profile = profilesById.get(lyraId)
      return { displayName: profile?.display_name || unknownProvider, photoUrl: profile?.profile_image_url ?? '' }
    }
    const hasProfile = (lyraId: string) => profilesById.has(lyraId)

    return { getProviderProfile, hasProfile }
  }, [careHistory, intl])

  return { ...resolver, isFetching, isLoading: isFetching && !careHistory }
}
